import React from 'react';
import { useTranslation } from '../contexts/TranslationContext';
import { formatBytes, formatSpeed, getProgress, getStatusDisplayText } from '../utils/formatters';

/**
 * UploadItem component
 * Displays individual upload item with progress, controls, and status
 */
function UploadItem({
  upload,
  selectedProject,
  getProjectName,
  startUpload,
  resumeUpload,
  pauseUpload,
  cancelUpload,
  deleteUpload,
  dismissError,
}) {
  const { t } = useTranslation();
  const progress = getProgress(upload.uploadedBytes, upload.fileSize);

  return (
    <div 
      className={`upload-item ${upload.projectId === selectedProject ? 'current-project' : ''}`}
    >
      <div className="upload-header">
        <span className="upload-name">
          {upload.fileName} → <span className="project-name">{getProjectName(upload.projectId)}</span>
          {upload.projectId === selectedProject && (
            <span className="current-project-badge">{t('current')}</span>
          )}
        </span>
        <div className="upload-header-right">
          <span className={`upload-status ${upload.status}`}>
            {getStatusDisplayText(upload.status, t)}
          </span>
          <button
            className="delete-button"
            onClick={() => deleteUpload(upload)}
            title={t('removeFromList')}
            aria-label={t('removeFromList')}
          >
            ×
          </button>
        </div>
      </div>
      <div className="upload-progress">
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
          <span className="progress-text">
            {formatBytes(upload.uploadedBytes)} / {formatBytes(upload.fileSize)} ({progress}%)
          </span>
          {upload.status === 'uploading' && upload.currentSpeed > 0 && (
            <span className="upload-speed" style={{ 
              fontSize: '12px', 
              color: '#666', 
              fontWeight: '500',
              marginLeft: '12px'
            }}>
              {formatSpeed(upload.currentSpeed)}
            </span>
          )}
        </div>
      </div>
      {upload.error && (
        <div className="upload-error">
          <span>{t('errorLabel')} {upload.error}</span>
          <button
            className="error-close-button"
            onClick={() => dismissError(upload)}
            title={t('dismissError')}
            aria-label={t('dismissError')}
          >
            ×
          </button>
        </div>
      )}
      {/* Hide control buttons if error occurred during initialization (upload never started) */}
      {!(upload.status === 'error' && !upload.serverSessionId) && (
        <div className="upload-controls">
          <button
            className={`control-button ${
              upload.status === 'ready' ? 'resume' : 
              (upload.status === 'paused' || upload.status === 'error') ? 'resume' : 'pause'
            }`}
            onClick={() => {
              if (upload.status === 'ready') {
                startUpload(upload);
              } else if (upload.status === 'paused' || upload.status === 'error') {
                resumeUpload(upload);
              } else if (upload.status === 'uploading') {
                pauseUpload(upload);
              }
            }}
            disabled={upload.status === 'completed' || upload.status === 'cancelled' || upload.status === 'initializing' || upload.status === 'queued'}
          >
            {upload.status === 'ready' ? t('upload') : 
             (upload.status === 'paused' || upload.status === 'error') ? t('resume') : 
             upload.status === 'queued' ? t('queued') : t('pause')}
          </button>
          {/* Show Cancel button for queued, uploading, paused, error (not for ready, completed, cancelled) */}
          {(upload.status === 'queued' || upload.status === 'uploading' || upload.status === 'paused' || upload.status === 'error') && (
            <button
              className="control-button cancel"
              onClick={() => cancelUpload(upload)}
              disabled={upload.status === 'completed' || upload.status === 'cancelled'}
            >
              {t('cancel')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default UploadItem;

